import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../../../components/common/Logo';

export default function DashboardPreview() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { id: 'home', icon: 'ri-home-5-line', label: t('dashboard.nav.dashboard') },
    { id: 'reviews', icon: 'ri-star-line', label: t('dashboard.nav.reviews') },
    { id: 'requests', icon: 'ri-send-plane-line', label: t('dashboard.nav.requests') },
    { id: 'shared', icon: 'ri-share-line', label: t('dashboard.nav.shared') },
    { id: 'widgets', icon: 'ri-layout-grid-line', label: t('dashboard.nav.widgets') },
    { id: 'google', icon: 'ri-google-fill', label: t('dashboard.nav.googleProfile') },
  ];

  const bottomNavItems = [
    { id: 'ai-agent', icon: 'ri-magic-line', label: t('dashboard.nav.aiAgent') },
    { id: 'settings', icon: 'ri-settings-3-line', label: t('dashboard.nav.settings') },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="animate-in fade-in duration-500">
            {/* Top Stats Row */}
            <div className="flex gap-4 mb-6 text-sm text-slate-500 border-b border-slate-100 pb-2 overflow-x-auto no-scrollbar">
              {['dashboard.home.nav.overview', 'dashboard.home.nav.performance', 'dashboard.home.nav.sentiment', 'dashboard.home.nav.competitors'].map((key, i) => (
                <button
                  key={key}
                  className={`font-medium pb-2 -mb-2.5 transition-colors whitespace-nowrap ${i === 0 ? 'text-[#0F172A] border-b-2 border-tron-primary' : 'hover:text-[#0F172A]'}`}
                >
                  {t(key)}
                </button>
              ))}
            </div>

            {/* Goal Card */}
            {/* Goal Card - Enhanced Contrast */}
            <div className="bg-gradient-to-r from-white to-emerald-50 rounded-xl p-6 mb-8 border border-emerald-100 border-l-[6px] border-l-tron-secondary shadow-md relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100/50 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-emerald-200/50"></div>

              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-full bg-white border-2 border-emerald-100 flex items-center justify-center shadow-sm text-tron-secondary">
                    <i className="ri-trophy-fill text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-tron-secondary uppercase tracking-wider mb-1">NEXT GOAL</div>
                    <div className="text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Inter Tight, sans-serif' }}>Achieve 5.0 rating</div>
                    <div className="text-sm text-slate-600">You need <span className="text-tron-secondary font-bold">42</span> more 5-star reviews to reach the next level</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-tron-secondary hover:border-emerald-200 hover:shadow-md transition-all"><i className="ri-magic-line"></i></button>
                  <button className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] hover:border-blue-200 hover:shadow-md transition-all"><i className="ri-send-plane-fill"></i></button>
                  <button className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:border-slate-300 hover:shadow-md transition-all"><i className="ri-qr-code-line"></i></button>
                </div>
              </div>
            </div>

            {/* 3 Grid Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300 group">
                <div className="flex justify-between items-start mb-4">
                  <span className="font-bold text-[#0F172A] group-hover:text-tron-secondary transition-colors">{t('dashboard.stat1.label')}</span>
                  <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-[#FABB05] group-hover:bg-[#FABB05] group-hover:text-white transition-all shadow-sm"><i className="ri-star-fill text-lg"></i></div>
                </div>
                <div className="text-4xl font-bold text-[#0F172A] mb-2">4.8</div>
                <div className="text-xs text-green-600 flex items-center gap-1 font-medium bg-green-50 w-fit px-2 py-0.5 rounded-full"><i className="ri-arrow-right-up-line"></i> {t('dashboard.stat1.change')}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all duration-300 group">
                <div className="flex justify-between items-start mb-4">
                  <span className="font-bold text-[#0F172A] group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors">{t('dashboard.stats.aggregatedReviews')}</span>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#10B981] to-[#1E3A8A] flex items-center justify-center text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] group-hover:bg-blue-900 group-hover:text-white transition-all shadow-sm"><i className="ri-chat-1-fill text-lg"></i></div>
                </div>
                <div className="text-4xl font-bold text-[#0F172A] mb-2">124</div>
                <div className="text-xs text-green-600 flex items-center gap-1 font-medium bg-green-50 w-fit px-2 py-0.5 rounded-full"><i className="ri-arrow-right-up-line"></i> {t('dashboard.stat2.change')}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm hover:shadow-lg hover:border-green-200 transition-all duration-300 group">
                <div className="flex justify-between items-start mb-4">
                  <span className="font-bold text-[#0F172A] group-hover:text-green-600 transition-colors">{t('dashboard.stats.privateFeedback')}</span>
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 group-hover:bg-green-600 group-hover:text-white transition-all shadow-sm"><i className="ri-message-2-fill text-lg"></i></div>
                </div>
                <div className="text-4xl font-bold text-[#0F172A] mb-2">8</div>
                <div className="text-xs text-green-600 flex items-center gap-1 font-medium bg-green-50 w-fit px-2 py-0.5 rounded-full"><i className="ri-arrow-right-up-line"></i> {t('dashboard.stat2.change')}</div>
              </div>
            </div>

            {/* Platform Stats */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <span className="font-bold text-[#0F172A]">Google</span>
                <div className="ml-auto text-slate-400 font-bold"><i className="ri-google-fill text-xl"></i></div>
              </div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <div className="text-sm text-slate-500 mb-1">{t('dashboard.stats.reviews')}</div>
                  <div className="text-3xl font-bold text-[#0F172A]">124</div>
                  <div className="text-xs text-green-600 mt-1">{t('dashboard.stat2.change')}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-500 mb-1">{t('dashboard.stats.rating')}</div>
                  <div className="text-3xl font-bold text-[#0F172A]">4.8</div>
                  <div className="text-xs text-green-600 mt-1">{t('dashboard.stat1.change')}</div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center text-sm font-medium text-slate-600 border border-slate-100">
                <i className="ri-trophy-line mr-2 text-white"></i> {t('dashboard.stats.goal')}
              </div>
            </div>
          </div>
        );

      case 'reviews':
        return (
          <div className="animate-in fade-in duration-500">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#0F172A]">{t('dashboard.reviews.title')}</h3>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:text-[#0F172A] flex items-center gap-2 shadow-sm transition-all hover:border-slate-300">
                  <i className="ri-filter-3-line text-slate-400"></i> {t('dashboard.reviews.filter')}
                </button>
                <button className="px-3 py-1.5 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-lg text-sm text-white hover:bg-blue-900 flex items-center gap-2 shadow-md shadow-tron-primary/10 transition-all">
                  <i className="ri-magic-line"></i> {t('dashboard.reviews.quickRequest')}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { name: 'Dieter Linder', date: t('dashboard.reviews.review1.date'), rating: 5, text: t('dashboard.reviews.review1.text'), replyDate: t('dashboard.reviews.review1.replyDate'), reply: t('dashboard.reviews.review1.reply') },
                { name: 'Sandra Johnson', date: t('dashboard.reviews.review2.date'), rating: 5, text: t('dashboard.reviews.review2.text'), replyDate: t('dashboard.reviews.review2.replyDate'), reply: t('dashboard.reviews.review2.reply') },
                { name: 'Carsten Bezuk', date: t('dashboard.reviews.review3.date'), rating: 5, text: t('dashboard.reviews.review3.text'), replyDate: t('dashboard.reviews.review3.replyDate'), reply: t('dashboard.reviews.review3.reply') }
              ].map((review, idx) => (
                <div key={idx} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:border-blue-200 transition-colors transition-all duration-300">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-tron-secondary flex items-center justify-center text-white font-bold text-sm">
                        {review.name.charAt(0)}{review.name.split(' ')[1]?.charAt(0)}
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-0.5">{review.date}</div>
                        <div className="font-bold text-[#0F172A] text-sm">{review.name}</div>
                        <div className="flex text-[#FABB05] text-xs mt-0.5">
                          {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 text-slate-400">
                      <i className="ri-share-line hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] cursor-pointer transition-colors"></i>
                      <i className="ri-delete-bin-line hover:text-red-500 cursor-pointer transition-colors"></i>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4 leading-relaxed">{review.text}</p>

                  <div className="bg-slate-50 rounded-lg p-3 ml-4 border-l-2 border-green-500">
                    <div className="flex items-center gap-2 text-xs text-green-600 mb-1 font-medium">
                      <i className="ri-check-line text-sm"></i> {review.replyDate}
                    </div>
                    <p className="text-sm text-slate-600 italic leading-relaxed">{review.reply}</p>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button className="text-xs font-semibold text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] bg-slate-50 px-3 py-1.5 rounded-lg transition-all border border-slate-100 hover:border-blue-200">{t('dashboard.reviews.editReply')}</button>
                    <button className="text-xs font-semibold text-red-500/70 hover:text-red-600 px-3 py-1.5 transition-colors">{t('dashboard.reviews.deleteReply')}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'requests':
        return (
          <div className="animate-in fade-in duration-500 h-full flex flex-col">
            <h3 className="text-xl font-bold text-[#0F172A] mb-6">{t('dashboard.requests.title')}</h3>
            <div className="flex flex-col md:flex-row h-full gap-6 md:gap-0">
              <div className="w-full md:w-48 md:border-r border-slate-200 md:pr-4 flex md:flex-col overflow-x-auto md:overflow-visible gap-2 md:space-y-1 pb-2 md:pb-0 no-scrollbar">
                {[t('dashboard.requests.send'), t('dashboard.requests.templates'), t('dashboard.requests.contacts'), t('dashboard.requests.optIn'), t('dashboard.requests.autoCampaign')].map((item, i) => (
                  <div key={i} className={`px-3 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${item === t('dashboard.requests.templates') ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 shadow-sm' : 'text-slate-500 hover:text-[#0F172A] hover:bg-slate-100'}`}>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex-1 md:pl-6">
                <div className="flex items-center gap-2 mb-6">
                  <h4 className="text-lg font-bold text-[#0F172A]">{t('dashboard.requests.templates')}</h4>
                  <i className="ri-refresh-line text-slate-400 cursor-pointer hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors"></i>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-blue-300 hover:bg-gradient-to-r from-[#10B981] to-[#1E3A8A] transition-all group min-h-[160px]">
                    <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform border border-slate-100">
                      <i className="ri-add-line text-2xl text-slate-400 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A]"></i>
                    </div>
                    <span className="text-sm font-medium text-slate-500 group-hover:text-[#0F172A]">{t('dashboard.requests.newTemplate')}</span>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-slate-200 relative group hover:border-blue-300 transition-all min-h-[160px] flex flex-col shadow-sm">
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold uppercase rounded-full border border-green-200">{t('dashboard.requests.active')}</div>
                    <div className="font-bold text-[#0F172A] mb-1">{t('dashboard.requests.campaignNameMock')}</div>
                    <div className="text-xs text-slate-500 mb-4 flex items-center gap-1"><i className="ri-calendar-line"></i> {t('dashboard.requests.dateMock')}</div>
                    <div className="bg-slate-50 rounded-lg p-3 mb-3 border border-slate-100">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">{t('dashboard.requests.channel')}</div>
                      <div className="flex items-center gap-2 text-sm text-slate-700"><i className="ri-mail-line text-red-500"></i> {t('dashboard.requests.email')}</div>
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-2">
                      <button className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 flex items-center gap-1 transition-all">
                        <i className="ri-eye-line text-white"></i> {t('dashboard.requests.preview')}
                      </button>
                      <div className="flex gap-2 text-slate-400">
                        <i className="ri-file-copy-line hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] cursor-pointer transition-colors"></i>
                        <i className="ri-delete-bin-line hover:text-red-500 cursor-pointer transition-colors"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-green-50 border border-green-100 rounded-xl p-3 flex items-center gap-3 animate-in slide-in-from-bottom-2 fade-in">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs min-w-[20px] shadow-sm shadow-green-200"><i className="ri-check-line"></i></div>
                  <span className="text-sm font-medium text-green-700">{t('dashboard.requests.success')}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'shared':
        return (
          <div className="animate-in fade-in duration-500 h-full flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full flex items-center justify-center mb-6 shadow-sm border border-blue-100">
              <i className="ri-share-line text-4xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-2">{t('dashboard.shared.title')}</h3>
            <p className="text-sm text-slate-500 max-w-sm mb-8 leading-relaxed">
              {t('dashboard.shared.desc')}
            </p>
            <button className="px-8 py-3 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:bg-blue-900 text-white font-bold rounded-xl transition-all shadow-lg shadow-tron-primary/20 cursor-pointer transform hover:scale-105 active:scale-95">
              {t('dashboard.shared.cta')}
            </button>
          </div>
        );

      case 'widgets':
        return (
          <div className="animate-in fade-in duration-500">
            <h3 className="text-xl font-bold text-[#0F172A] mb-6">{t('dashboard.widgets.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: t('dashboard.widgets.carousel.title'), icon: 'ri-slideshow-3-line', desc: t('dashboard.widgets.carousel.desc') },
                { title: t('dashboard.widgets.list.title'), icon: 'ri-list-check-2', desc: t('dashboard.widgets.list.desc') },
                { title: t('dashboard.widgets.badge.title'), icon: 'ri-medal-line', desc: t('dashboard.widgets.badge.desc') },
                { title: t('dashboard.widgets.popup.title'), icon: 'ri-notification-badge-line', desc: t('dashboard.widgets.popup.desc') }
              ].map((widget, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 transition-all group shadow-sm">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-blue-100 shadow-sm">
                    <i className={`${widget.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="font-bold text-[#0F172A] mb-2">{widget.title}</h4>
                  <p className="text-sm text-slate-500 mb-6 leading-relaxed">{widget.desc}</p>
                  <div className="flex gap-3">
                    <button className="flex-1 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:bg-blue-100 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-sm font-bold py-2 rounded-lg transition-colors border border-blue-200">
                      {t('dashboard.widgets.preview')}
                    </button>
                    <button className="flex-1 bg-white hover:bg-slate-50 text-[#0F172A] text-sm font-bold py-2 rounded-lg transition-colors border border-slate-200">
                      {t('dashboard.widgets.install')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'google':
        return (
          <div className="animate-in fade-in duration-500">
            <div className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-200 rounded-xl p-6 mb-8 flex items-center gap-6 shadow-sm">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center p-3 shadow-md border border-slate-100">
                <img src="/images/google-g-logo.png" alt="Google" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = 'https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg' }} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-[#0F172A]">{t('dashboard.mock.company')}</h3>
                  <i className="ri-verified-badge-fill text-white text-xl"></i>
                </div>
                <p className="text-slate-500 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  {t('dashboard.mock.connected')}
                </p>
              </div>
              <button className="ml-auto px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold rounded-lg border border-slate-200 transition-colors shadow-sm">
                {t('dashboard.mock.manage')}
              </button>
            </div>

            <h4 className="text-lg font-bold text-[#0F172A] mb-4">{t('dashboard.profile.performance')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="text-sm text-slate-500 mb-1">{t('dashboard.profile.views')}</div>
                <div className="text-2xl font-bold text-[#0F172A]">12.4K</div>
                <div className="text-xs text-green-600 mt-1 font-medium">+12% {t('dashboard.profile.thisMonth')}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="text-sm text-slate-500 mb-1">{t('dashboard.profile.clicks')}</div>
                <div className="text-2xl font-bold text-[#0F172A]">482</div>
                <div className="text-xs text-green-600 mt-1 font-medium">+5% {t('dashboard.profile.thisMonth')}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="text-sm text-slate-500 mb-1">{t('dashboard.profile.calls')}</div>
                <div className="text-2xl font-bold text-[#0F172A]">64</div>
                <div className="text-xs text-red-500 mt-1 font-medium">-2% {t('dashboard.profile.thisMonth')}</div>
              </div>
            </div>
          </div>
        );

      case 'ai-agent':
        return (
          <div className="animate-in fade-in duration-500">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-1">{t('dashboard.ai.title')}</h3>
                <p className="text-sm text-slate-500">{t('dashboard.ai.subtitle')}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-[#0F172A]">{t('dashboard.ai.autoReply')}</span>
                <div className="w-12 h-6 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full p-1 cursor-pointer border border-blue-300 relative transition-all">
                  <div className="w-4 h-4 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full shadow-sm shadow-tron-primary/30 absolute right-1"></div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-[#0F172A] mb-4">{t('dashboard.ai.tonality')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[t('dashboard.ai.tone.professional'), t('dashboard.ai.tone.friendly'), t('dashboard.ai.tone.empathic')].map((tone, i) => (
                    <div key={i} className={`p-4 rounded-xl border cursor-pointer transition-all ${i === 1 ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border-tron-primary text-[#0F172A]' : 'bg-slate-50 border-slate-200 text-slate-400 hover:border-blue-300'}`}>
                      <div className="font-bold mb-1">{tone}</div>
                      <div className="text-xs opacity-70">{t('dashboard.ai.tone.example')}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h4 className="font-bold text-[#0F172A] mb-4">{t('dashboard.ai.signature')}</h4>
                <textarea
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-[#0F172A] focus:border-tron-primary focus:outline-none min-h-[80px] transition-all"
                  defaultValue={t('dashboard.ai.signature.placeholder')}
                />
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="animate-in fade-in duration-500">
            <h3 className="text-xl font-bold text-[#0F172A] mb-6">{t('dashboard.settings.title')}</h3>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-48 flex md:flex-col overflow-x-auto md:overflow-visible gap-2 md:space-y-1 pb-2 md:pb-0 no-scrollbar">
                {[t('dashboard.settings.general'), t('dashboard.settings.notifications'), t('dashboard.settings.team'), t('dashboard.settings.billing'), t('dashboard.settings.integrations')].map((item, i) => (
                  <div key={i} className={`px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${i === 0 ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 shadow-sm' : 'text-slate-500 hover:text-[#0F172A] hover:bg-slate-100'}`}>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex-1 bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
                <h4 className="font-bold text-[#0F172A] mb-6 border-b border-slate-100 pb-4">{t('dashboard.settings.generalInfo')}</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1 tracking-wider uppercase">{t('dashboard.settings.companyName')}</label>
                    <input type="text" defaultValue={t('dashboard.mock.company')} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm text-[#0F172A] focus:border-tron-primary focus:outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1 tracking-wider uppercase">{t('dashboard.settings.website')}</label>
                    <input type="text" defaultValue={`https://${t('dashboard.mock.domain')}`} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm text-[#0F172A] focus:border-tron-primary focus:outline-none transition-all" />
                  </div>
                  <div className="pt-4">
                    <button className="px-6 py-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white font-bold text-sm rounded-lg hover:bg-blue-900 transition-colors w-full sm:w-auto shadow-md shadow-tron-primary/10">
                      {t('dashboard.settings.save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-slate-400 text-center py-20 animate-in fade-in">
            <i className="ri-tools-line text-4xl mb-4 block opacity-50"></i>
            <p>Coming Soon</p>
          </div>
        );
    }
  };

  return (
    <section className="py-16 md:py-32 bg-gradient-to-b from-[#10172A] to-[#12192D] relative overflow-hidden border-t border-slate-800/50">
      {/* Enhanced decorative elements with animation */}
      <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/10 to-emerald-500/10 rounded-full blur-[100px]"></div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-12 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 backdrop-blur-sm rounded-full mb-6 border border-slate-700/50 shadow-sm">
            <div className="w-2 h-2 bg-gradient-to-r from-[#10B981] to-[#3B82F6] rounded-full animate-pulse shadow-[0_0_10px_rgba(14,165,233,0.5)]"></div>
            <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#3B82F6]" style={{ fontFamily: 'DM Sans, sans-serif' }}>{t('dashboard.badge')}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight px-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            <span className="bg-gradient-to-r from-[#10B981] to-[#60A5FA] bg-clip-text text-transparent">{t('dashboard.title')} {t('dashboard.titleHighlight')}</span>
          </h2>
          <p className="text-base md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed px-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Enhanced Browser Window Mockup */}
        <div className="relative max-w-6xl mx-auto group">
          {/* Subtle Outer Glow */}
          <div className="absolute -inset-4 md:-inset-10 bg-gradient-to-r from-blue-400/30 via-emerald-400/30 to-blue-400/30 rounded-[2.5rem] blur-3xl opacity-60 group-hover:opacity-80 transition-opacity duration-500 animate-pulse"></div>

          <div className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl shadow-slate-300/50 overflow-hidden transform group-hover:scale-[1.01] transition-all duration-500 border border-slate-200 ring-1 ring-slate-900/5">
            {/* Enhanced Browser Chrome */}
            <div className="bg-slate-50/90 backdrop-blur-md px-3 md:px-4 py-2 md:py-3 flex items-center gap-2 border-b border-slate-200">
              <div className="flex gap-1.5 md:gap-2">
                <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full shadow-sm bg-[#FF5F56] border border-[#E0443E]"></div>
                <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full shadow-sm bg-[#FFBD2E] border border-[#DEA123]"></div>
                <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full shadow-sm bg-[#27C93F] border border-[#1AAB29]"></div>
              </div>
              <div className="flex-1 mx-2 md:mx-4 hidden sm:block">
                <div className="bg-white rounded-lg px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-slate-500 flex items-center gap-2 shadow-sm border border-slate-200 ring-1 ring-slate-900/5">
                  <i className="ri-lock-fill text-white"></i>
                  <span className="truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>app.reputrust.ai/dashboard</span>
                </div>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="flex flex-col lg:flex-row bg-white text-[#0F172A] min-h-[600px]">
              {/* Sidebar */}
              <div className="hidden lg:flex flex-col w-64 bg-slate-50 border-r border-slate-200 p-4 backdrop-blur-sm">
                <Logo
                  className="flex items-center gap-2 mb-8 px-2"
                  iconClassName="w-8 h-8 shadow-sm"
                  textClassName="text-sm font-bold tracking-tight"
                />

                <nav className="space-y-1 flex-1">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left ${activeTab === item.id
                        ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 shadow-sm'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A] border border-transparent'
                        }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      <span className="text-sm font-medium" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item.label}</span>
                    </button>
                  ))}

                  <div className="my-4 border-t border-slate-200 mx-2"></div>

                  {bottomNavItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left ${activeTab === item.id
                        ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A] border border-transparent'
                        }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      <span className="text-sm font-medium" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item.label}</span>
                    </button>
                  ))}
                </nav>

                <div className="mt-auto pt-4 px-2 border-t border-slate-200">
                  <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 cursor-pointer">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-lg flex items-center justify-center text-white font-bold text-xs">MK</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-[#0F172A] truncate">{t('dashboard.mock.company')}</div>
                      <div className="text-[10px] text-slate-500 truncate">{t('dashboard.mock.domain')}</div>
                    </div>
                    <i className="ri-expand-up-down-line text-slate-400 text-xs"></i>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 flex flex-col bg-white bg-[url('/images/grid.svg')] bg-opacity-5 relative overflow-hidden">
                {/* Header inside Content */}
                <div className="h-16 border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 bg-white/80 backdrop-blur-md relative z-20">
                  <div className="font-bold text-[#0F172A] text-lg truncate pr-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                    {activeTab === 'home' && t('dashboard.nav.dashboard')}
                    {activeTab === 'reviews' && t('dashboard.nav.reviews')}
                    {activeTab === 'requests' && t('dashboard.nav.requests')}
                    {activeTab === 'shared' && t('dashboard.nav.shared')}
                    {activeTab === 'widgets' && t('dashboard.nav.widgets')}
                    {activeTab === 'google' && t('dashboard.nav.googleProfile')}
                    {activeTab === 'settings' && t('dashboard.nav.settings')}
                    {activeTab === 'ai-agent' && t('dashboard.nav.aiAgent')}
                  </div>

                  {/* Hamburger Menu Button */}
                  <button
                    className="lg:hidden p-2 text-slate-500 hover:text-[#0F172A]"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  >
                    <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
                  </button>
                </div>

                {/* Mobile Menu Overlay */}
                <AnimatePresence>
                  {isMobileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="absolute inset-0 top-16 z-30 bg-white/95 backdrop-blur-xl p-4 lg:hidden overflow-y-auto"
                    >
                      <nav className="space-y-1">
                        {navItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              setActiveTab(item.id);
                              setIsMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 text-left ${activeTab === item.id
                              ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 shadow-sm'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A] border border-transparent'
                              }`}
                          >
                            <i className={`${item.icon} text-lg`}></i>
                            <span className="text-sm font-medium">{item.label}</span>
                          </button>
                        ))}

                        <div className="my-4 border-t border-slate-100 mx-2"></div>

                        {bottomNavItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              setActiveTab(item.id);
                              setIsMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 text-left ${activeTab === item.id
                              ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-100 shadow-sm'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A] border border-transparent'
                              }`}
                          >
                            <i className={`${item.icon} text-lg`}></i>
                            <span className="text-sm font-medium">{item.label}</span>
                          </button>
                        ))}
                      </nav>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex-1 p-6 overflow-y-auto custom-scrollbar relative z-10">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12 md:mt-16">
          <p className="text-slate-400 text-xs md:text-sm font-medium" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('dashboard.footer')}
          </p>
        </div>
      </div>
    </section>
  );
}
